import { FeedbackLine } from "./FeedbackLine";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Group, Loader, Paper, Stack, Text, Title } from "@mantine/core";
import { qs, type ApiError } from "../api/client";
import {
  useApproveMobileAttempt,
  useIdentities,
  useLogout,
  useMe,
  useMobileAttempt,
} from "../api/queries/auth";
import { displayNameOf } from "../api/types";
import { Header } from "./Header";
import { LoginDialog } from "./LoginDialog";
import { Shell } from "./Shell";

const ATTEMPT_PATTERN = /^[A-Za-z0-9_-]{43}$/;

function fixedCallback(value: string): string | null {
  try {
    const url = new URL(value);
    const release =
      url.protocol === "https:" &&
      url.hostname === "jtarchive.net" &&
      url.port === "" &&
      url.pathname === "/mobile/return";
    const debug =
      url.protocol === "net.jtarchive.app.debug:" &&
      url.hostname === "mobile" &&
      url.port === "" &&
      url.pathname === "/return";
    const code = url.searchParams.getAll("code");
    const state = url.searchParams.getAll("state");
    if (
      (!release && !debug) ||
      url.username !== "" ||
      url.password !== "" ||
      url.hash !== "" ||
      code.length !== 1 ||
      state.length !== 1 ||
      !ATTEMPT_PATTERN.test(code[0]!) ||
      !ATTEMPT_PATTERN.test(state[0]!) ||
      [...url.searchParams.keys()].length !== 2
    ) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function MobileAuthorizePage({
  navigate = (target: string) => window.location.assign(target),
}: { navigate?: (target: string) => void } = {}) {
  const { t } = useTranslation();
  const attemptIds = new URLSearchParams(window.location.search).getAll("attempt_id");
  const id =
    attemptIds.length === 1 && ATTEMPT_PATTERN.test(attemptIds[0]!) ? attemptIds[0]! : null;
  const me = useMe();
  const attempt = useMobileAttempt(id);
  const approve = useApproveMobileAttempt();
  const logout = useLogout();
  const [loginOpen, setLoginOpen] = useState(false);
  const [unsafeCallback, setUnsafeCallback] = useState(false);

  const metadata = attempt.data;
  const expired = metadata !== undefined && Date.parse(metadata.expires_at) <= Date.now();
  const mismatch =
    me.data !== null &&
    me.data !== undefined &&
    metadata?.expected_account_id !== null &&
    metadata?.expected_account_id !== me.data.id;
  const needsProvider =
    metadata?.purpose === "account" && metadata.provider !== null && me.data != null && !mismatch;
  const identities = useIdentities(needsProvider);
  const providerLinked =
    !needsProvider || identities.data?.some((identity) => identity.provider === metadata.provider);
  const invalid = id === null || attempt.isError || expired;

  return (
    <Shell>
      <Shell.Header>
        <Header />
      </Shell.Header>
      <Shell.Main style={{ padding: 20 }}>
        <Paper p="lg" maw={560} mx="auto">
          <Stack gap="md">
            <Title order={2}>{t("mobileAuthorize.heading")}</Title>
            {id !== null && (attempt.isPending || me.isPending) ? (
              <Loader size="sm" />
            ) : invalid ? (
              <Alert color="red">{t("mobileAuthorize.invalid")}</Alert>
            ) : (
              <>
                <Text>
                  {t(
                    metadata?.purpose === "account"
                      ? "mobileAuthorize.accountPurpose"
                      : "mobileAuthorize.loginPurpose",
                  )}
                </Text>
                {me.data ? (
                  <Text>{t("mobileAuthorize.signedInAs", { name: displayNameOf(me.data) })}</Text>
                ) : (
                  <Button onClick={() => setLoginOpen(true)}>{t("mobileAuthorize.signIn")}</Button>
                )}

                {mismatch && (
                  <Alert color="yellow">
                    <Stack gap="sm">
                      <Text>{t("mobileAuthorize.mismatch")}</Text>
                      <Button
                        variant="default"
                        loading={logout.isPending}
                        onClick={() => logout.mutate()}
                      >
                        {t("mobileAuthorize.changeAccount")}
                      </Button>
                    </Stack>
                  </Alert>
                )}

                {!mismatch && me.data && needsProvider && (
                  <FeedbackLine role={identities.isError ? "alert" : "status"}>
                    {identities.isPending ? (
                      <Loader size={14} />
                    ) : identities.isError ? (
                      t("mobileAuthorize.invalid")
                    ) : null}
                  </FeedbackLine>
                )}
                {!mismatch &&
                  me.data &&
                  needsProvider &&
                  identities.isSuccess &&
                  !providerLinked && (
                    <Stack gap="sm">
                      <Text>{t("mobileAuthorize.waitingForProvider")}</Text>
                      <Button
                        component="a"
                        href={`/api/auth/sso/${metadata?.provider}/start${qs({ mobile_attempt_id: id })}`}
                      >
                        {t("mobileAuthorize.continueWith", {
                          provider: t(`sso.${metadata?.provider}`),
                        })}
                      </Button>
                    </Stack>
                  )}

                <FeedbackLine role={approve.error || unsafeCallback ? "alert" : "status"}>
                  {metadata?.approved && (
                    <Text inherit c="accent">
                      {t("mobileAuthorize.approved")}
                    </Text>
                  )}
                  {(approve.error || unsafeCallback) &&
                    (unsafeCallback
                      ? t("mobileAuthorize.invalid")
                      : (approve.error as ApiError).detail)}
                </FeedbackLine>
                <Group justify="flex-end">
                  <Button component="a" href="/" variant="default">
                    {t("common.cancel")}
                  </Button>
                  {!mismatch && me.data && providerLinked && !metadata?.approved && (
                    <Button
                      loading={approve.isPending}
                      onClick={() =>
                        id &&
                        approve.mutate(id, {
                          onSuccess: ({ return_url }) => {
                            const target = fixedCallback(return_url);
                            if (target === null) setUnsafeCallback(true);
                            else navigate(target);
                          },
                        })
                      }
                    >
                      {approve.isPending
                        ? t("mobileAuthorize.approving")
                        : t("mobileAuthorize.approve")}
                    </Button>
                  )}
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      </Shell.Main>
      {loginOpen && (
        <LoginDialog
          onClose={() => setLoginOpen(false)}
          mobileAttemptId={id ?? undefined}
          allowedProviders={metadata?.provider ? [metadata.provider] : undefined}
        />
      )}
    </Shell>
  );
}
